import { useState } from 'react';
import { ActionIcon, Box, Button, Group, Text } from '@mantine/core';
import { IconRefresh, IconX } from '@tabler/icons-react';
import { useVersionCheck } from '../../hooks/useVersionCheck';

/**
 * UpdateBanner - Non-blocking "a new version is available" banner.
 *
 * Polls /version.json via useVersionCheck and, when a newer build is detected,
 * shows a dismissible banner across the top of the app with a Refresh button.
 * We deliberately never auto-reload: the user reloads when they're ready, so an
 * in-progress form or unsaved edit is never destroyed by a surprise refresh.
 *
 * Rendered once, globally (see App.tsx). No-ops in local dev.
 */
export function UpdateBanner() {
  const { updateAvailable, reload } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000, // above app chrome; below modals/overlays
        backgroundColor: '#2D4739', // Medley primary
        color: '#fff',
        padding: '8px 16px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      <Group justify="center" gap="md" wrap="nowrap">
        <Text size="sm" fw={500} c="#fff">
          A new version of Medley is available.
        </Text>
        <Button
          size="xs"
          variant="white"
          color="dark"
          leftSection={<IconRefresh size={14} />}
          onClick={reload}
          styles={{ root: { fontWeight: 600 } }}
        >
          Refresh
        </Button>
        <ActionIcon
          variant="subtle"
          color="gray.0"
          aria-label="Dismiss update notice"
          onClick={() => setDismissed(true)}
          style={{ color: '#fff', position: 'absolute', right: 12 }}
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>
    </Box>
  );
}
